import React, { createContext, useContext, useReducer, useEffect, useRef, useMemo } from "react";
import { saveDraft, getDraft } from "../lib/api";

const DRAFT_ID_KEY = "onefeather_draft_id";

function getDraftId() {
  let id = localStorage.getItem(DRAFT_ID_KEY);
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
    localStorage.setItem(DRAFT_ID_KEY, id);
  }
  return id;
}

const initialEvent = {
  eventTitle: "",
  membershipSize: "",
  electionType: "",
  governingRegulation: "",
  electronicVoting: "",
  internationalVoters: "0",
  pollingLocations: "1",
  startDate: "",
  endDate: "",
  voterTurnout: "60",
  location: "",
  preparedBy: "",
};

const initialLead = {
  name: "",
  email: "",
  jobTitle: "",
  firstNation: "",
  street: "",
  city: "",
  province: "",
  postalCode: "",
  phone: "",
  organization: "",
  consentAccepted: false,
};

const initialState = {
  step: 1,
  event: initialEvent,
  items: null,
  lead: initialLead,
  document: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      return { ...initialState, ...action.state };
    case "SET_STEP":
      return { ...state, step: action.step };
    case "UPDATE_EVENT":
      return { ...state, event: { ...state.event, ...action.payload } };
    case "SET_EVENT":
      return { ...state, event: { ...state.event, ...action.payload } };
    case "SET_ITEMS":
      return { ...state, items: action.items };
    case "SET_DOCUMENT":
      return { ...state, document: action.document };
    case "UPDATE_LEAD":
      return { ...state, lead: { ...state.lead, ...action.payload } };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

const ProposalContext = createContext(null);

export function ProposalProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const draftId = useRef(getDraftId());
  const hydrated = useRef(false);
  const saveTimer = useRef(null);

  // Load the server-side draft once on mount (lead/PII is never stored in the draft).
  useEffect(() => {
    (async () => {
      try {
        const data = await getDraft(draftId.current);
        if (data) dispatch({ type: "HYDRATE", state: data });
      } catch (e) {
        console.debug("No existing draft found; starting fresh.", e);
      } finally {
        hydrated.current = true;
      }
    })();
  }, []);

  // Debounced sync of the non-sensitive draft (step, event, items, document) to the server.
  useEffect(() => {
    if (!hydrated.current) return;
    const { step, event, items, document } = state;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveDraft(draftId.current, { step, event, items, document }).catch(() => {});
    }, 600);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persist only when state changes
  }, [state]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return (
    <ProposalContext.Provider value={value}>
      {children}
    </ProposalContext.Provider>
  );
}

export function useProposal() {
  const ctx = useContext(ProposalContext);
  if (!ctx) throw new Error("useProposal must be used within ProposalProvider");
  return ctx;
}
